import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, useMemo } from "react";
import { Search as SearchIcon, ExternalLink } from "lucide-react";
import type { Release, StreamingPlatform } from "@/lib/content";
import { STREAMING_LINKS, formatReleaseDate, getCurrentRelease, getNextRelease } from "@/lib/content";
import { getReleases } from "@/lib/music.functions";
import { ReleaseCover } from "@/components/site/ReleaseCover";
import { SectionHeader } from "@/components/site/SectionHeader";
import { SITE_URL, absoluteUrl } from "@/lib/site";
import {
  MUSIC_CATALOG_ID,
  breadcrumbs,
  discographyGraph,
  itemList,
  ldScript,
  orgRef,
  pageNode,
  personRef,
} from "@/lib/schema";

import buddhaKaImage from "@/assets/buddha-ka-official.png.asset.json";

const PORTRAIT_URL = absoluteUrl(buddhaKaImage.url);
const PAGE_URL = `${SITE_URL}/music`;
const PAGE_TITLE = "Buddha KA Music | Albums, Singles & KA Flow";
const PAGE_DESCRIPTION =
  "The official Buddha KA music catalog from KAfrequency — albums, singles, and the sutra cycle, written in KA Flow: hip-hop and soul fused with spoken wisdom.";

export const Route = createFileRoute("/music/")({
  loader: async () => ({ releases: await getReleases() }),
  component: MusicArchive,
  errorComponent: () => (
    <div className="mx-auto max-w-3xl px-4 py-24 text-center">
      <p className="text-gold">The archive is temporarily unavailable.</p>
      <Link to="/" className="mt-4 inline-block text-gold-luminous">← Return home</Link>
    </div>
  ),
  notFoundComponent: () => (
    <div className="mx-auto max-w-3xl px-4 py-24 text-center">
      <p className="text-gold">Nothing found.</p>
    </div>
  ),
  head: ({ loaderData }) => {
    const releases = (loaderData?.releases ?? []) as Release[];
    return {
      meta: [
        { title: PAGE_TITLE },
        { name: "description", content: PAGE_DESCRIPTION },
        { property: "og:title", content: PAGE_TITLE },
        { property: "og:description", content: PAGE_DESCRIPTION },
        { property: "og:type", content: "website" },
        { property: "og:url", content: PAGE_URL },
        { property: "og:image", content: PORTRAIT_URL },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: PAGE_TITLE },
        { name: "twitter:description", content: PAGE_DESCRIPTION },
        { name: "twitter:image", content: PORTRAIT_URL },
      ],
      links: [
        { rel: "canonical", href: PAGE_URL },
        // Machine-readable discography for agents and crawlers.
        {
          rel: "alternate",
          type: "application/ld+json",
          href: `${SITE_URL}/music/catalog.json`,
          title: "Buddha KA discography (JSON-LD)",
        },
      ],
      scripts: [
        ldScript(
          pageNode({
            type: "CollectionPage",
            id: MUSIC_CATALOG_ID,
            url: PAGE_URL,
            name: "Buddha KA Music Catalog",
            description: PAGE_DESCRIPTION,
            image: PORTRAIT_URL,
            imageCaption: "Buddha KA — official portrait",
            about: [personRef(), orgRef()],
            extra: {
              creator: personRef(),
              publisher: orgRef(),
              mainEntity: itemList(
                releases.map((r) => ({ path: `/music/${r.slug}`, name: r.title })),
              ),
            },
          }),
        ),
        // The discography itself: artist node patched with album/track, plus
        // one MusicAlbum / MusicRecording node per release.
        ldScript(discographyGraph(releases)),
        ldScript(breadcrumbs([["Music", "/music"]])),
      ],
    };
  },

});

const TYPES = ["All", "Album", "Single", "EP", "Mixtape"] as const;

const GROUPS: { key: Release["status"]; label: string; blurb: string }[] = [
  { key: "released", label: "Released", blurb: "Available now across streaming platforms." },
  { key: "upcoming", label: "Upcoming", blurb: "Announced and scheduled." },
  { key: "in-progress", label: "In the studio", blurb: "Being written, recorded, or mixed." },
];

const ARTIST_PLATFORMS: StreamingPlatform[] = ["Spotify", "Apple Music", "YouTube", "SoundCloud"];

function ReleaseCard({ r }: { r: Release }) {
  return (
    <Link to="/music/$slug" params={{ slug: r.slug }} className="group">
      <ReleaseCover coverUrl={r.coverUrl} label={`${r.title} cover`} alt={`${r.title} cover art`} />
      <div className="mt-4 grid grid-cols-[minmax(0,1fr)_auto] items-baseline gap-3">
        <h3 className="truncate font-serif text-2xl group-hover:text-gold-luminous">{r.title}</h3>
        {r.year && <span className="shrink-0 text-xs text-muted-foreground">{r.year}</span>}
      </div>
      <p className="text-xs text-gold">
        {r.type}
        {r.frequency ? ` · ${r.frequency}` : ""}
      </p>
      <p className="mt-3 text-sm text-muted-foreground line-clamp-2">{r.statement}</p>
    </Link>
  );
}

function MusicArchive() {
  const { releases } = Route.useLoaderData() as { releases: Release[] };
  const nextRelease = useMemo(() => getNextRelease(releases), [releases]);
  const currentRelease = useMemo(() => getCurrentRelease(releases), [releases]);
  // Promote the next dated release until it lands; afterwards the newest live
  // release holds the slot as "Out now".
  const spotlight = nextRelease ?? currentRelease;
  const [q, setQ] = useState("");
  const [type, setType] = useState<(typeof TYPES)[number]>("All");
  const [freq, setFreq] = useState<string>("All");

  const frequencies = useMemo(
    () => ["All", ...Array.from(new Set(releases.map((r) => r.frequency).filter(Boolean)))],
    [releases],
  );

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return releases.filter((r) => {
      if (type !== "All" && r.type !== type) return false;
      if (freq !== "All" && r.frequency !== freq) return false;
      if (needle && !`${r.title} ${r.statement} ${r.mood.join(" ")}`.toLowerCase().includes(needle)) return false;
      return true;
    });
  }, [q, type, freq, releases]);

  const grouped = useMemo(
    () => GROUPS.map((g) => ({ ...g, items: filtered.filter((r) => r.status === g.key) })).filter((g) => g.items.length > 0),
    [filtered],
  );

  const artistLinks = ARTIST_PLATFORMS.map((p) => ({ platform: p, url: STREAMING_LINKS[p] })).filter(
    (x): x is { platform: StreamingPlatform; url: string } => Boolean(x.url),
  );

  return (
    <>
      <section className="relative border-b hairline">
        <div className="absolute inset-0 -z-10 halo opacity-40" aria-hidden />
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 sm:py-28">
          <p className="text-xs uppercase tracking-[0.3em] text-gold">The Canon</p>
          <h1 className="mt-4 font-serif text-5xl sm:text-6xl md:text-7xl">Buddha KA Music</h1>
          <p className="mt-6 max-w-2xl text-lg text-muted-foreground">
            Albums, singles, and transmissions from Buddha KA — the independent Chicago-born artist and founder of
            KAfrequency.
          </p>
          <p className="mt-4 max-w-2xl text-base text-muted-foreground">
            The catalog is written in <strong className="text-ivory">KA Flow</strong>, Buddha KA&rsquo;s own framework:
            hip-hop cadence and soul/R&amp;B melody carrying spoken wisdom, spiritual science, and cinematic world-building
            in a single record.
          </p>
          <p className="mt-4 text-sm">
            <Link to="/artist/buddha-ka" className="text-gold underline decoration-gold/40 underline-offset-4 hover:text-gold-luminous">
              Official Buddha KA Artist Page
            </Link>{" "}
            <span className="text-muted-foreground">— verified streaming profiles, current release, and discography.</span>
          </p>



          {artistLinks.length > 0 && (
            <div className="mt-8 flex flex-wrap gap-2" aria-label="Official artist profiles">
              {artistLinks.map(({ platform, url }) => (
                <a
                  key={platform}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Buddha KA on ${platform}`}
                  className="inline-flex items-center gap-1 rounded-full border hairline px-4 py-2 text-xs text-muted-foreground hover:border-gold hover:text-gold"
                >
                  {platform} <ExternalLink size={10} aria-hidden />
                </a>
              ))}
            </div>
          )}
        </div>
      </section>

      {spotlight && (
        <section
          aria-labelledby="next-release-heading"
          className="mx-auto mt-12 max-w-7xl px-4 sm:px-6"
        >
          <div className="grid gap-8 rounded-xl border border-gold/30 bg-charcoal p-6 sm:p-8 md:grid-cols-[minmax(0,240px)_minmax(0,1fr)] md:items-center">
            <Link to="/music/$slug" params={{ slug: spotlight.slug }} className="block">
              <ReleaseCover
                coverUrl={spotlight.coverUrl}
                label={`${spotlight.title} cover`}
                alt={`${spotlight.title} cover art`}
              />
            </Link>
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-gold">{nextRelease ? "Next release" : "Out now"}</p>
              <h2 id="next-release-heading" className="mt-3 font-serif text-4xl">
                <Link to="/music/$slug" params={{ slug: spotlight.slug }} className="hover:text-gold-luminous">
                  {spotlight.title}
                </Link>
              </h2>
              <p className="mt-2 text-sm text-gold">
                {spotlight.type}
                {spotlight.releaseDate ? (
                  <>
                    {nextRelease ? " · Releasing " : " · Released "}
                    <time dateTime={spotlight.releaseDate}>{formatReleaseDate(spotlight)}</time>
                  </>
                ) : null}
              </p>
              <p className="mt-4 max-w-2xl text-sm text-muted-foreground">{spotlight.statement}</p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link
                  to="/music/$slug"
                  params={{ slug: spotlight.slug }}
                  className="inline-flex min-h-11 items-center rounded-full bg-gold-gradient px-5 py-2.5 text-sm font-semibold text-obsidian shadow-gold"
                >
                  {spotlight.title} release page
                </Link>
                {nextRelease?.preSaveUrl && (
                  <a
                    href={nextRelease.preSaveUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex min-h-11 items-center gap-1 rounded-full border border-gold px-5 py-2.5 text-sm text-gold hover:bg-gold hover:text-obsidian"
                  >
                    Pre-save <ExternalLink size={12} aria-hidden />
                  </a>
                )}
              </div>
            </div>
          </div>
        </section>
      )}



      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6">
        <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-center">
          <label className="relative block">
            <span className="sr-only">Search music</span>
            <SearchIcon size={16} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" aria-hidden />
            <input
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search titles, moods, statements…"
              className="w-full rounded-full border hairline bg-obsidian py-3 pl-11 pr-4 text-sm placeholder:text-muted-foreground focus:border-gold focus:outline-none focus:ring-2 focus:ring-gold"
            />
          </label>
          <div className="flex flex-wrap gap-2">
            {TYPES.map((t) => (
              <button
                key={t}
                onClick={() => setType(t)}
                className={`rounded-full border px-4 py-2 text-xs transition-colors ${
                  type === t ? "border-gold bg-gold text-obsidian" : "hairline text-muted-foreground hover:border-gold hover:text-gold"
                }`}
              >
                {t}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-6 flex flex-wrap items-center gap-2">
          <span className="mr-2 text-xs uppercase tracking-[0.25em] text-muted-foreground">Frequency</span>
          {frequencies.map((f) => (
            <button
              key={f}
              onClick={() => setFreq(f)}
              className={`rounded-full border px-3 py-1.5 text-[11px] transition-colors ${
                freq === f ? "border-gold bg-gold text-obsidian" : "hairline text-muted-foreground hover:border-gold hover:text-gold"
              }`}
            >
              {f}
            </button>
          ))}
        </div>

        {grouped.length === 0 ? (
          <div className="mt-16 rounded-xl border hairline bg-charcoal p-12 text-center">
            <p className="font-serif text-2xl text-gold-luminous">The field is quiet on that query.</p>
            <p className="mt-2 text-sm text-muted-foreground">Try a different frequency, type, or word.</p>
          </div>
        ) : (
          <div className="mt-14 space-y-16">
            {grouped.map((g) => (
              <section key={g.key} aria-labelledby={`group-${g.key}`}>
                <div className="flex flex-wrap items-baseline gap-3">
                  <h2 id={`group-${g.key}`} className="font-serif text-3xl">{g.label}</h2>
                  <p className="text-sm text-muted-foreground">{g.blurb}</p>
                </div>
                <div className="mt-8 grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
                  {g.items.map((r) => (
                    <ReleaseCard key={r.slug} r={r} />
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}

        <div className="mt-20 rounded-xl border hairline bg-charcoal p-8">
          <SectionHeader eyebrow="Continue" title="Where the music connects." />
          <ul className="mt-6 grid gap-3 text-sm text-muted-foreground sm:grid-cols-2">
            <li>
              <Link to="/about" className="text-gold underline decoration-gold/40 underline-offset-4 hover:text-gold-luminous hover:decoration-gold">Buddha KA biography</Link> — the origin of
              KA Flow and the KAfrequency ecosystem.
            </li>
            <li>
              <Link to="/press" className="text-gold underline decoration-gold/40 underline-offset-4 hover:text-gold-luminous hover:decoration-gold">Press kit</Link> — official bios, portrait,
              and selected works for media.
            </li>
            <li>
              <Link to="/news" className="text-gold underline decoration-gold/40 underline-offset-4 hover:text-gold-luminous hover:decoration-gold">Official newsroom</Link> — release
              announcements and project updates.
            </li>
            <li>
              <Link to="/activations" className="text-gold underline decoration-gold/40 underline-offset-4 hover:text-gold-luminous hover:decoration-gold">Daily activations</Link> — the
              practice side of the same body of work.
            </li>
            <li>
              <Link to="/books" className="text-gold underline decoration-gold/40 underline-offset-4 hover:text-gold-luminous hover:decoration-gold">Books</Link> — the written companion to the
              sutra cycle.
            </li>
            <li>
              <Link to="/contact" className="text-gold underline decoration-gold/40 underline-offset-4 hover:text-gold-luminous hover:decoration-gold">Contact</Link> — booking, licensing, and
              press enquiries.
            </li>
          </ul>
        </div>
      </section>
    </>
  );
}
